import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, hydrateAfterLogin } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use useRef flag to prevent StrictMode double-execution
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Read the session token from BOTH the hash and the query string.
      // We prefer the hash for security (fragments aren't sent to the
      // server / referrer), but iPhone Safari sometimes strips fragments
      // from 302-redirect Location headers — when that happens the query
      // string is our fallback. Either way we immediately scrub both from
      // the URL bar via replaceState so the token never persists in
      // browser history.
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      const hashTokenMatch = hash.match(/session_token=([^&]+)/);
      const queryTokenMatch = search.match(/[?&]session_token=([^&]+)/);
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      const sessionTokenMatch = hashTokenMatch || queryTokenMatch;

      // Best-effort diagnostic: tell the backend what the browser arrived
      // with, so when sign-in mysteriously fails on a mobile device we can
      // look at server logs and see whether the fragment/query was stripped.
      // Use sendBeacon (built for fire-and-forget logging during page
      // transitions) so iPhone Safari doesn't drop it when we navigate away.
      try {
        const diag = {
          ua: navigator.userAgent,
          has_hash: Boolean(hash),
          has_search: Boolean(search),
          has_hash_token: Boolean(hashTokenMatch),
          has_query_token: Boolean(queryTokenMatch),
          has_session_id: Boolean(sessionIdMatch),
          referrer: document.referrer || null,
        };
        const url = `${apiClient.defaults.baseURL}/auth/diag/callback-arrival`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify(diag)], { type: "application/json" }));
        } else {
          fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(diag),
            keepalive: true,
          }).catch(() => {});
        }
      } catch { /* ignore */ }

      // Magic-link / Google-OAuth path: backend has already issued our
      // session token and redirected here with #session_token=X (and a
      // ?session_token=X query-string fallback for iPhone Safari).
      if (sessionTokenMatch) {
        const token = sessionTokenMatch[1];
        try {
          localStorage.setItem("session_token", token);
          const meRes = await apiClient.get("/auth/me");
          const userObj = meRes.data;
          let redirectTo = "/dashboard";
          if (userObj?.is_super_admin && !userObj?.impersonating) redirectTo = "/admin";

          // Hydrate AuthContext in memory then router-navigate. Crucially
          // we do NOT reload the page — iPhone Safari can lose the
          // freshly-written localStorage between location.replace() and
          // the next checkAuth(), which would silently log the user out
          // right after a successful sign-in.
          await hydrateAfterLogin(userObj);
          window.history.replaceState(null, "", redirectTo);
          navigate(redirectTo, { replace: true, state: { user: userObj, authenticated: true } });
        } catch (e) {
          console.error("Magic-link callback failed:", e);
          setError(e?.response?.data?.detail || "Sign-in failed. Please request a new link.");
          setTimeout(() => navigate("/", { replace: true }), 3000);
        }
        return;
      }

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          // Capture the original path before clearing hash (e.g., /invite/{token})
          const originalPath = window.location.pathname;
          const user = await login(sessionId);

          // Decide post-login destination: invite link wins, then super admin
          // workspace, then default app dashboard.
          let redirectTo;
          if (originalPath.startsWith('/invite/')) {
            redirectTo = originalPath;
          } else if (user?.is_super_admin && !user?.impersonating) {
            redirectTo = '/admin';
          } else {
            redirectTo = '/dashboard';
          }

          // Clear the hash from URL
          window.history.replaceState(null, '', redirectTo);
          // Small delay to ensure token is set
          await new Promise(resolve => setTimeout(resolve, 100));
          navigate(redirectTo, { state: { user, authenticated: true }, replace: true });
        } catch (error) {
          console.error("Auth failed:", error);
          const detail = error?.response?.data?.detail || "Authentication failed. Please try again.";
          setError(detail);
          setTimeout(() => navigate('/', { replace: true }), 3000);
        }
      } else {
        // No token in hash or query — log loudly and bounce home with a
        // visible message so the user knows we never received the token.
        console.warn("[AuthCallback] no token in URL", { hash: hash.slice(0, 80), search: search.slice(0, 80) });
        setError(`No sign-in token received. URL state: hash=${hash ? "present" : "empty"}, query=${search ? "present" : "empty"}`);
        setTimeout(() => navigate("/", { replace: true }), 4000);
      }
    };

    processAuth();
  }, [login, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-12 h-12 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-700 font-medium">{error || "Authenticating..."}</p>
        {error && (
          <p className="text-xs text-slate-400 mt-3" data-testid="auth-callback-debug">
            You'll be returned home in a moment. If this keeps happening on
            iPhone Safari, please screenshot this page and share with support.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
